/**
 * Session recording for free-trial Companion sessions.
 *
 * LogRocket replays a session by recording the DOM, so what it captures is whatever was on screen.
 * On the Companion that would include customer PHA scenarios, project and facility names and the
 * whole chat transcript — which is why this only ever runs for free trial accounts. A trial account
 * reaches the Sample company plus Industry and LabPHA data, all of which is RiskAlive's own or
 * anonymised, so nothing belonging to a customer is recorded.
 *
 * The trial decision is NOT made here. It is passed in from the server's own answer
 * (/api/chat/trial-status → isTrialMode), which is derived from JwtCompanyResolver and correctly
 * excludes both the full company-ai-chat role and the metered company-ai-chat-limited one. That
 * rule already exists in three places; a fourth copy here would be one more thing to drift.
 *
 * Nothing is downloaded unless every gate passes — the SDK is injected on demand rather than
 * shipped in the bundle, so a paying customer's browser never fetches it at all.
 */

/**
 * The LogRocket app id, in the form 'org/project'. Not a secret: it ships in the page source, the
 * same way the gtag measurement id in index.html does.
 *
 * This is the company marketing account's project, which is the one whose seats and retention the
 * marketing team actually pays for — the earlier value pointed at a different org and would have
 * recorded into a project nobody watches.
 *
 * Everything here no-ops while this is empty, so the file can be merged and deployed ahead of the
 * account existing and switched on with a one-line change.
 */
const APP_ID = 'shlzye/risk-alive-ai-ric';

/** CDN build of the SDK. Sphinx has no npm of its own, so it is injected rather than bundled. */
const SDK_URL = 'https://cdn.lr-ingest.com/LogRocket.min.js';

/**
 * Hosts where recording is allowed. Mirrors the gtag gate in Sphinx.Client/wwwroot/index.html.
 *
 * DEV ONLY, DELIBERATELY. The production hosts (sphinx.riskalive.com, pha.riskalive.com) are held
 * out until the go-live prerequisites in PRIVACY_LOGROCKET_WORDING.md are done: the LogRocket DPA
 * signed, the privacy policy actually describing session replay, retention set to 30 days, and
 * dashboard seats restricted. None of those are code changes, and none were finished when the app
 * id landed on main — so this list, not the app id, is what keeps recording off production.
 *
 * Adding the two production hosts back IS the go-live switch. Do not add them to make something
 * work; check the list in PRIVACY_LOGROCKET_WORDING.md first.
 *
 * Note that simply reverting the commit that added sphinx-dev would be worse than leaving it: it
 * removes the dev host and leaves the production ones, which turns recording on for real visitors.
 */
const PRODUCTION_HOSTS = ['sphinx-dev.riskalive.com'];

/** True on a host in PRODUCTION_HOSTS above — currently Dev only. False everywhere else. */
const isProductionHost = () => {
    const hostname = window.location.hostname.toLowerCase();
    return PRODUCTION_HOSTS.some((host) => hostname.endsWith(host));
};

/**
 * Whether the visitor looks European, used to skip recording until a data processing agreement with
 * LogRocket is in place. Kept in step with the copy in Lorax (src/base/analytics/logrocket.js).
 *
 * Timezone only. An earlier version also treated any -GB/-DE/-FR style region subtag in
 * navigator.languages as European, which excluded most of Canada: en-GB is routinely present in a
 * Canadian browser's language list. navigator.languages is a statement of language preference, not
 * of location, so it is not evidence of anything here.
 *
 * Still biased towards over-excluding — an unresolvable timezone counts as European — because a
 * false positive costs one unrecorded session while a false negative records an EU resident without
 * a DPA. Remove the whole check once the DPA is signed.
 */
const looksEuropean = () => {
    // European timezones that are not named Europe/*. All are EEA territories.
    const europeanZones = [
        'Atlantic/Canary',    // Spain
        'Atlantic/Madeira',   // Portugal
        'Atlantic/Azores',    // Portugal
        'Atlantic/Faroe',     // Denmark
        'Atlantic/Reykjavik', // Iceland
    ];

    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        // No timezone means no way to tell, so fail closed and skip recording. Every browser we
        // support resolves one, so this costs nothing in practice.
        if (!timeZone) return true;
        return timeZone.startsWith('Europe/') || europeanZones.includes(timeZone);
    } catch {
        return true;
    }
};

/**
 * Whether the tester explicitly asked to be recorded, via ?record=force.
 *
 * The EU exclusion fails closed and says nothing: a European timezone, the five Atlantic EEA
 * zones, and any timezone that cannot be resolved all skip recording silently. A tester on a
 * European machine therefore sees no sessions at all and reasonably concludes the integration is
 * broken. This is the documented way to say "record me anyway" while testing.
 *
 * It cannot switch recording on in production. PRODUCTION_HOSTS above contains no production host,
 * so recording never starts there whatever the query string says - and the person adding the flag
 * is consenting to their own session being recorded, which a real visitor could not.
 *
 * @returns {boolean} True when ?record=force is present.
 */
const recordingForced = () => {
    try {
        return new URLSearchParams(window.location.search).get('record') === 'force';
    } catch {
        return false;
    }
};

/**
 * Strips credentials out of recorded network traffic. Our bearer tokens last twelve hours, so a
 * replay containing one is a replay containing a live session.
 */
const sanitizers = {
    dom: {
        inputSanitizer: true,
    },
    network: {
        requestSanitizer: (request) => {
            if (request.headers) {
                Object.keys(request.headers)
                    .filter((key) => key.toLowerCase() === 'authorization')
                    .forEach((key) => {
                        request.headers[key] = '';
                    });
            }
            return request;
        },
        responseSanitizer: (response) => {
            // Token-bearing endpoints. The chat responses are left intact: for a trial account the
            // answers are drawn from Sample and anonymised data, and they are most of the value.
            if ((response.url || '').toLowerCase().includes('/api/account/')) {
                return { ...response, body: null };
            }
            return response;
        },
    },
};

/** Injects the SDK once, resolving when window.LogRocket is available. */
const loadSdk = () => new Promise((resolve, reject) => {
    if (window.LogRocket) {
        resolve(window.LogRocket);
        return;
    }

    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => (window.LogRocket ? resolve(window.LogRocket) : reject(new Error('SDK did not attach')));
    script.onerror = () => reject(new Error('SDK failed to load'));
    document.head.appendChild(script);
});

/**
 * Names the session after the signed-in user, so a replay can be tied back to a support
 * conversation. Reads the same decoded token Intercom is identified from
 * (window.riskalive.intercom.update), which riskalive.js exposes.
 */
const identify = async (LogRocket) => {
    try {
        if (!window.riskalive || !window.riskalive.sso) return;

        const token = await window.riskalive.sso.decodeToken();
        if (!token || !token.UserId) return;

        const name = [token.FirstName, token.LastName].filter(Boolean).join(' ');
        LogRocket.identify(token.UserId, {
            email: token.Email || '',
            name: name || token.Email || '',
            company: token.CompanyName || '',
            plan: 'trial',
        });
    } catch {
        // An unidentified session is still worth having.
    }
};

let started = false;

/**
 * Starts recording when the caller is a free trial account and every gate passes. Safe to call
 * repeatedly — it initialises at most once per page load.
 *
 * @param {boolean} isTrial The server's isTrialMode from /api/chat/trial-status. The only source of
 *   truth for this decision; do not substitute a client-side role check.
 */
const startIfTrial = async (isTrial) => {
    if (started) return;

    // Each gate says why it declined. Silent returns cost real time on Dev: every one of these
    // looks identical from the outside - no sessions in the dashboard - and they have very
    // different fixes.
    if (!APP_ID) {
        console.debug('[LogRocket] Not started: no app id configured.');
        return;
    }
    if (isTrial !== true) {
        console.debug('[LogRocket] Not started: not a free-trial account (isTrialMode is %o).', isTrial);
        return;
    }
    if (!isProductionHost()) {
        console.debug(
            '[LogRocket] Not started: %s is not in PRODUCTION_HOSTS (currently Dev only).',
            window.location.hostname,
        );
        return;
    }
    if (looksEuropean() && !recordingForced()) {
        console.debug(
            '[LogRocket] Not started: visitor looks European (timezone %s) and no DPA is in place. '
            + 'Append ?record=force to record yourself while testing.',
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
        return;
    }

    console.debug('[LogRocket] Starting session recording.');
    started = true;
    try {
        const LogRocket = await loadSdk();
        LogRocket.init(APP_ID, sanitizers);
        await identify(LogRocket);
    } catch {
        // Analytics must never break the chat surface it is measuring.
        started = false;
    }
};

export default { startIfTrial };
